import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || '';

function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [search, setSearch] = useState('');
  const [importingExcel, setImportingExcel] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [newEmployee, setNewEmployee] = useState({
    email: '',
    nombre: '',
    departamento: '',
    tipo: 'colaborador'
  });
  const [editEmployee, setEditEmployee] = useState({
    id: null,
    email: '',
    nombre: '',
    departamento: '',
    tipo: 'colaborador'
  });
  const [bulkText, setBulkText] = useState('');
  const [bulkEditData, setBulkEditData] = useState({
    departamento: '',
    tipo: ''
  });

  const fetchEmployees = async () => {
    const token = localStorage.getItem('adminToken');
    const res = await fetch(`${API_URL}/api/admin/employees`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setEmployees(await res.json());
    setLoading(false);
  };

  useEffect(() => {
    fetchEmployees();
    fetch(`${API_URL}/api/survey/structure`)
      .then(res => res.json())
      .then(data => setDepartamentos(data.departamentos));
  }, []);

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    try {
      const res = await fetch(`${API_URL}/api/admin/employees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newEmployee)
      });
      if (res.ok) {
        setShowAddModal(false);
        setNewEmployee({ email: '', nombre: '', departamento: '', tipo: 'colaborador' });
        fetchEmployees();
      } else {
        const data = await res.json();
        alert(data.error || 'Error al añadir empleado');
      }
    } catch (err) {
      alert('Error de conexión');
    }
  };

  const handleBulkImport = async () => {
    const lines = bulkText.split('\n').filter(l => l.trim());
    const employees = lines.map(line => {
      const [email, nombre, departamento, tipo] = line.split(';').map(s => s.trim());
      return { email, nombre, departamento, tipo: tipo || 'colaborador' };
    }).filter(e => e.email);

    if (employees.length === 0) {
      alert('No se encontraron empleados válidos');
      return;
    }

    const token = localStorage.getItem('adminToken');
    try {
      const res = await fetch(`${API_URL}/api/admin/employees/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ employees })
      });
      const data = await res.json();
      alert(`Se importaron ${data.inserted} empleados`);
      setShowBulkModal(false);
      setBulkText('');
      fetchEmployees();
    } catch (err) {
      alert('Error al importar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este empleado?')) return;
    const token = localStorage.getItem('adminToken');
    await fetch(`${API_URL}/api/admin/employees/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchEmployees();
  };

  const handleToggleActive = async (id) => {
    const token = localStorage.getItem('adminToken');
    await fetch(`${API_URL}/api/admin/employees/${id}/toggle-active`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchEmployees();
  };

  const handleOpenEdit = (emp) => {
    setEditEmployee({
      id: emp.id,
      email: emp.email,
      nombre: emp.nombre || '',
      departamento: emp.departamento || '',
      tipo: emp.tipo || 'colaborador'
    });
    setShowEditModal(true);
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    try {
      const res = await fetch(`${API_URL}/api/admin/employees/${editEmployee.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          nombre: editEmployee.nombre,
          email: editEmployee.email,
          departamento: editEmployee.departamento,
          tipo: editEmployee.tipo
        })
      });
      if (res.ok) {
        setShowEditModal(false);
        fetchEmployees();
      } else {
        const data = await res.json();
        alert(data.error || 'Error al actualizar empleado');
      }
    } catch (err) {
      alert('Error de conexión');
    }
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportingExcel(true);
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/admin/employees/import-excel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        alert(`Importación completada:\n- Importados: ${data.imported}\n- Omitidos (duplicados): ${data.skipped}\n- Errores: ${data.errors.length}`);
        fetchEmployees();
      } else {
        alert('Error: ' + (data.error || 'Error al importar'));
      }
    } catch (err) {
      alert('Error de conexión al importar');
    } finally {
      setImportingExcel(false);
      e.target.value = '';
    }
  };

  // Bulk selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredEmployees.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredEmployees.map(e => e.id));
    }
  };

  const handleBulkAction = async (action, data) => {
    const token = localStorage.getItem('adminToken');
    try {
      const res = await fetch(`${API_URL}/api/admin/employees/bulk-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selectedIds, action, data })
      });
      if (res.ok) {
        setSelectedIds([]);
        fetchEmployees();
      } else {
        const result = await res.json();
        alert(result.error || 'Error en la acción masiva');
      }
    } catch (err) {
      alert('Error de conexión');
    }
  };

  const handleBulkDelete = () => {
    if (!window.confirm(`¿Eliminar ${selectedIds.length} empleados seleccionados?`)) return;
    handleBulkAction('delete');
  };

  const handleBulkActivate = () => handleBulkAction('activate');
  const handleBulkDeactivate = () => handleBulkAction('deactivate');

  const handleBulkEditSubmit = () => {
    const data = {};
    if (bulkEditData.departamento) data.departamento = bulkEditData.departamento;
    if (bulkEditData.tipo) data.tipo = bulkEditData.tipo;
    if (Object.keys(data).length === 0) {
      alert('Selecciona al menos un campo para modificar');
      return;
    }
    handleBulkAction('update', data);
    setShowBulkEditModal(false);
    setBulkEditData({ departamento: '', tipo: '' });
  };

  const filteredEmployees = employees.filter(emp =>
    emp.email.toLowerCase().includes(search.toLowerCase()) ||
    (emp.nombre && emp.nombre.toLowerCase().includes(search.toLowerCase())) ||
    (emp.departamento && emp.departamento.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <AdminLayout title="Gestión de Empleados">
      <div className="page-header">
        <input
          type="text"
          placeholder="Buscar empleados..."
          className="search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="header-actions">
          <label
            className="btn btn-secondary"
            style={{ cursor: importingExcel ? 'wait' : 'pointer', opacity: importingExcel ? 0.7 : 1 }}
          >
            {importingExcel ? 'Importando...' : 'Importar Excel'}
            <input
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleExcelImport}
              disabled={importingExcel}
            />
          </label>
          <button
            className="btn btn-secondary"
            onClick={() => setShowBulkModal(true)}
          >
            Importar Lista
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            Añadir Empleado
          </button>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="card card--mb" style={{
          background: '#e8f4fd',
          border: '1px solid #b3d9f2',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <span style={{ fontWeight: 600, color: '#1a5276' }}>
            {selectedIds.length} empleado{selectedIds.length > 1 ? 's' : ''} seleccionado{selectedIds.length > 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              style={{ padding: '6px 14px', fontSize: '13px' }}
              onClick={() => {
                setBulkEditData({ departamento: '', tipo: '' });
                setShowBulkEditModal(true);
              }}
            >
              Editar seleccionados
            </button>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 14px', fontSize: '13px' }}
              onClick={handleBulkActivate}
            >
              Activar
            </button>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 14px', fontSize: '13px' }}
              onClick={handleBulkDeactivate}
            >
              Desactivar
            </button>
            <button
              className="delete-btn"
              style={{ padding: '6px 14px', fontSize: '13px' }}
              onClick={handleBulkDelete}
            >
              Eliminar
            </button>
            <button
              className="btn btn-cancel"
              style={{ padding: '6px 14px', fontSize: '13px' }}
              onClick={() => setSelectedIds([])}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="card card--mb">
        <h3 className="card-title">
          Empleados Registrados ({filteredEmployees.length})
        </h3>

        {loading ? (
          <div className="empty-state">Cargando...</div>
        ) : filteredEmployees.length === 0 ? (
          <div className="empty-state">No hay empleados registrados</div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.length === filteredEmployees.length && filteredEmployees.length > 0}
                      onChange={toggleSelectAll}
                      title="Seleccionar todos"
                    />
                  </th>
                  <th>Email</th>
                  <th>Nombre</th>
                  <th>Departamento</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map(emp => (
                  <tr
                    key={emp.id}
                    style={{
                      ...(emp.activo === 0 ? { opacity: 0.5, background: '#f8f8f8' } : {}),
                      ...(selectedIds.includes(emp.id) ? { background: '#e8f4fd' } : {})
                    }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(emp.id)}
                        onChange={() => toggleSelect(emp.id)}
                      />
                    </td>
                    <td>{emp.email}</td>
                    <td>{emp.nombre || '-'}</td>
                    <td>{emp.departamento || '-'}</td>
                    <td>
                      <span className={`badge ${emp.tipo === 'directivo' ? 'badge-directivo' : 'badge-colaborador'}`}>
                        {emp.tipo === 'directivo' ? 'Directivo' : 'Colaborador'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${emp.activo !== 0 ? 'badge-colaborador' : 'badge-directivo'}`}
                        style={emp.activo !== 0
                          ? { background: '#d4edda', color: '#155724' }
                          : { background: '#f8d7da', color: '#721c24' }
                        }
                      >
                        {emp.activo !== 0 ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-primary"
                        style={{ padding: '4px 10px', fontSize: '12px', marginRight: '5px' }}
                        onClick={() => handleOpenEdit(emp)}
                      >
                        Editar
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '12px', marginRight: '5px' }}
                        onClick={() => handleToggleActive(emp.id)}
                      >
                        {emp.activo !== 0 ? 'Desactivar' : 'Activar'}
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(emp.id)}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">Añadir Empleado</h2>
            <form className="form" onSubmit={handleAddEmployee}>
              <div className="input-group">
                <label className="label">Email *</label>
                <input
                  type="email"
                  className="input"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                  required
                />
              </div>
              <div className="input-group">
                <label className="label">Nombre</label>
                <input
                  type="text"
                  className="input"
                  value={newEmployee.nombre}
                  onChange={(e) => setNewEmployee({ ...newEmployee, nombre: e.target.value })}
                />
              </div>
              <div className="input-group">
                <label className="label">Departamento</label>
                <select
                  className="select"
                  value={newEmployee.departamento}
                  onChange={(e) => setNewEmployee({ ...newEmployee, departamento: e.target.value })}
                >
                  <option value="">Seleccionar...</option>
                  {departamentos.map(dep => (
                    <option key={dep} value={dep}>{dep}</option>
                  ))}
                </select>
              </div>
              <div className="input-group">
                <label className="label">Tipo</label>
                <select
                  className="select"
                  value={newEmployee.tipo}
                  onChange={(e) => setNewEmployee({ ...newEmployee, tipo: e.target.value })}
                >
                  <option value="colaborador">Colaborador</option>
                  <option value="directivo">Directivo</option>
                </select>
              </div>
              <div className="modal-buttons">
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Añadir
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">Editar Empleado</h2>
            <form className="form" onSubmit={handleEditEmployee}>
              <div className="input-group">
                <label className="label">Email</label>
                <input
                  type="email"
                  className="input"
                  value={editEmployee.email}
                  onChange={(e) => setEditEmployee({ ...editEmployee, email: e.target.value })}
                  required
                />
              </div>
              <div className="input-group">
                <label className="label">Nombre</label>
                <input
                  type="text"
                  className="input"
                  value={editEmployee.nombre}
                  onChange={(e) => setEditEmployee({ ...editEmployee, nombre: e.target.value })}
                />
              </div>
              <div className="input-group">
                <label className="label">Departamento</label>
                <select
                  className="select"
                  value={editEmployee.departamento}
                  onChange={(e) => setEditEmployee({ ...editEmployee, departamento: e.target.value })}
                >
                  <option value="">Sin departamento</option>
                  {departamentos.map(dep => (
                    <option key={dep} value={dep}>{dep}</option>
                  ))}
                </select>
              </div>
              <div className="input-group">
                <label className="label">Tipo</label>
                <select
                  className="select"
                  value={editEmployee.tipo}
                  onChange={(e) => setEditEmployee({ ...editEmployee, tipo: e.target.value })}
                >
                  <option value="colaborador">Colaborador</option>
                  <option value="directivo">Directivo</option>
                </select>
              </div>
              <div className="modal-buttons">
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkEditModal && (
        <div className="modal-overlay" onClick={() => setShowBulkEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">
              Editar {selectedIds.length} empleado{selectedIds.length > 1 ? 's' : ''}
            </h2>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
              Solo se modificarán los campos que selecciones. Los campos vacíos no se cambiarán.
            </p>
            <div className="form">
              <div className="input-group">
                <label className="label">Departamento</label>
                <select
                  className="select"
                  value={bulkEditData.departamento}
                  onChange={(e) => setBulkEditData({ ...bulkEditData, departamento: e.target.value })}
                >
                  <option value="">-- No cambiar --</option>
                  {departamentos.map(dep => (
                    <option key={dep} value={dep}>{dep}</option>
                  ))}
                </select>
              </div>
              <div className="input-group">
                <label className="label">Tipo</label>
                <select
                  className="select"
                  value={bulkEditData.tipo}
                  onChange={(e) => setBulkEditData({ ...bulkEditData, tipo: e.target.value })}
                >
                  <option value="">-- No cambiar --</option>
                  <option value="colaborador">Colaborador</option>
                  <option value="directivo">Directivo</option>
                </select>
              </div>
              <div className="modal-buttons">
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => setShowBulkEditModal(false)}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleBulkEditSubmit}
                >
                  Aplicar a {selectedIds.length} empleado{selectedIds.length > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">Importar Lista de Empleados</h2>
            <p className="bulk-import-hint">
              Formato: email;nombre;departamento;tipo (uno por línea)
              <br />
              Ejemplo: juan@emiliomoro.com;Juan García;Marketing;colaborador
            </p>
            <textarea
              className="textarea"
              placeholder="Pega aquí la lista de empleados..."
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
            />
            <div className="modal-buttons">
              <button
                className="btn btn-cancel"
                onClick={() => setShowBulkModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleBulkImport}
              >
                Importar
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminEmployees;
